import { Button, Column } from '@tempmee/react';
import { OutlineMailIcon } from '@tempmee/react-icons';
import { InviteCountCaption } from '../InviteCountCaption';
import { EmbeddedCandidateList } from './EmbeddedCandidateList';
import { InviteSuccessPopup } from './InviteSuccessPopup';
import { type IInterviewInviteTarget, useInterviewInvite } from './useInterviewInvite';

/** The confirmation after inviting candidates to an interview, wherever the invite was sent from. */
export const INTERVIEW_INVITE_SENT_COPY =
  'This interview has been successfully sent to your selected candidates.';

interface IInterviewInviteSectionProps {
  target: IInterviewInviteTarget;
  /** Replaces the confirmation popup after a send (see `useInterviewInvite`). */
  onInvited?: () => void;
}

/**
 * The interview invite: the inline candidate list card, an Invite button that
 * invites the selected candidates to this interview, and a confirmation popup on
 * success. The left column of the redesigned Interview Details page (perm onsite),
 * and the body of Posting Details' invite dialog. Logic lives in
 * `useInterviewInvite`; already-invited candidates render as non-selectable "Sent"
 * rows.
 */
export const InterviewInviteSection = ({ target, onInvited }: IInterviewInviteSectionProps) => {
  const {
    date,
    listKey,
    setSelectedIds,
    canInvite,
    isInviting,
    invitedCount,
    showSuccess,
    closeSuccess,
    invite,
  } = useInterviewInvite(target, { onInvited });

  return (
    <Column gap={6} data-cy="interview-invite-section" width="100%">
      <EmbeddedCandidateList
        key={listKey}
        postingId={target.postingId}
        interviewId={target.interviewId}
        date={date}
        onSelectionChange={setSelectedIds}
      />

      <Column align="center" gap={2}>
        <Button
          startIcon={<OutlineMailIcon size={20} />}
          data-cy="interview-invite-button"
          size="small"
          disabled={!canInvite}
          loading={isInviting}
          onClick={invite}
        >
          Invite
        </Button>
        <InviteCountCaption count={invitedCount} />
      </Column>

      {showSuccess && (
        <InviteSuccessPopup
          dataCy="interview-invite-success"
          description={INTERVIEW_INVITE_SENT_COPY}
          onClose={closeSuccess}
        />
      )}
    </Column>
  );
};
